import React, { useEffect, useMemo, useState } from 'react';
import { p, tp, useP } from '../../i18n/useP';
import { useTranslation } from 'react-i18next';
import {
  listHrContracts, peopleCostSynthesis, listHrLppRates,
  listPayrollMovements, createPayrollMovement, updatePayrollMovement,
  deletePayrollMovement, deleteAllPayrollMovements,
} from '../../api/hr';
import { formatNumber } from '../../utils/format';
import { contractMassInMonth, extraMonths } from './salaryMass';
import { namedMovementDelta } from '../treasury/engine/computePayrollFromHr';
import PageHeader from '../../components/PageHeader';
import './HrPage.css';

const DEFAULT_AMOUNT = 10000;

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function parseLocalDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function contractActiveInMonth(c, year, monthIdx) {
  const monthStart = new Date(year, monthIdx, 1);
  const monthEnd   = new Date(year, monthIdx + 1, 0);
  if (!c.start_date) return false;
  const start = parseLocalDate(c.start_date);
  if (start > monthEnd) return false;
  if (c.end_date) {
    const end = parseLocalDate(c.end_date);
    if (end < monthStart) return false;
  }
  return true;
}

function baseMonthlyMass(contracts, year, monthIdx, months = [12, 6]) {
  return contracts.reduce((acc, c) => acc + contractMassInMonth(c, year, monthIdx, months), 0);
}

/**
 * Rolling 12-month baseline starting at (startYear, startMonthIdx).
 * Returns array of 12 cells [{ year, monthIdx, base }, …].
 */
export function rolling12Baseline(contracts, startYear, startMonthIdx, months = [12, 6]) {
  const out = [];
  for (let k = 0; k < 12; k++) {
    const cellMonth = (startMonthIdx + k) % 12;
    const cellYear = startYear + Math.floor((startMonthIdx + k) / 12);
    out.push({
      year: cellYear,
      monthIdx: cellMonth,
      base: baseMonthlyMass(contracts, cellYear, cellMonth, months),
    });
  }
  return out;
}

/**
 * Compute the impact of a single movement on a given (year, monthIdx) cell.
 * - hire / departure : recurring from start_month onwards (+/- monthly_amount)
 * - bonus : one-shot on start_month
 */
export function movementImpactOnCell(mv, year, monthIdx) {
  const sd = parseLocalDate(mv.start_month);
  const ed = mv.end_month ? parseLocalDate(mv.end_month) : null;
  const sign = mv.movement_type === 'departure' ? -1 : +1;
  const amt = Number(mv.monthly_amount || 0) * sign;
  const cell = new Date(year, monthIdx, 1);
  if (mv.movement_type === 'bonus') {
    return (sd.getFullYear() === year && sd.getMonth() === monthIdx) ? amt : 0;
  }
  if (cell < sd) return 0;
  if (ed && cell > ed) return 0;
  return amt;
}

function ymToDateStr(year, monthIdx) {
  const mm = String(monthIdx + 1).padStart(2, '0');
  return `${year}-${mm}-01`;
}

function dateStrToYM(s) {
  const d = parseLocalDate(s);
  return { year: d.getFullYear(), monthIdx: d.getMonth() };
}

// Montant mensuel affiché pour un mouvement.
// Synthétique (sans person_id) → monthly_amount saisi.
// Nominatif (rate_change / departure lié à une personne) → delta réel calculé
// sur le mois de prise d'effet (salaire du contrat × variation de taux, ou
// salaire perdu au départ). Source unique = namedMovementDelta du moteur partagé.
function movementDisplayAmount(mv, contracts, extra) {
  if (!mv.person_id) return Number(mv.monthly_amount || 0);
  const { year, monthIdx } = dateStrToYM(mv.start_month);
  return namedMovementDelta(contracts, [mv], year, monthIdx, extra);
}

/* ── Page component ──────────────────────────────────────────────────────── */

export default function HrCostSimulationPage({
  activeTenant, setPage, embedded = false,
  // Fenêtre pilotée depuis l'extérieur (Planning → Set up) : mois/année de
  // départ du scénario. Quand fournis, ils remplacent le sélecteur local et la
  // page passe en lecture seule (reflet du planning People).
  startMonth, startYear: startYearProp,
}) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const today = new Date();
  const MONTHS_SHORT = React.useMemo(
    () => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)),
    [t],
  );
  const monthLabel = (monthIdx) => MONTHS_SHORT[monthIdx] || '';

  // Reflet en lecture seule quand la fenêtre vient du Set up.
  const externalWindow = Number.isInteger(startMonth);
  const readOnly = embedded && externalWindow;

  const [startYearState, setStartYear] = useState(today.getFullYear());
  const [startMonthState, setStartMonthIdx] = useState(today.getMonth());
  const startYear = externalWindow
    ? (Number.isInteger(startYearProp) ? startYearProp : today.getFullYear())
    : startYearState;
  const startMonthIdx = externalWindow ? startMonth : startMonthState;

  const [contracts, setContracts] = useState([]);
  const [synth, setSynth] = useState(null);
  const [movements, setMovements] = useState([]);
  const [lppRates, setLppRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  function refresh() {
    if (!tenantId) return;
    setLoading(true);
    Promise.all([
      listHrContracts(tenantId),
      peopleCostSynthesis(tenantId, startYear, 'all'),
      listPayrollMovements(tenantId),
      listHrLppRates(tenantId),
    ])
      .then(([c, s, m, lpp]) => {
        if (!c.error) setContracts(c.data || []);
        if (!s.error) setSynth(s.data || s);
        if (!m.error) setMovements(m.data || []);
        if (!lpp.error) setLppRates(lpp.data || []);
      })
      .finally(() => setLoading(false));
  }

  useEffect(refresh, [tenantId, startYear]); // eslint-disable-line

  // Baseline 12 mois roulants + effectif après mouvements (cell-by-cell)
  const baseline = useMemo(
    () => rolling12Baseline(contracts, startYear, startMonthIdx, extraMonths(activeTenant)),
    [contracts, startYear, startMonthIdx, activeTenant?.thirteenth_salary_month, activeTenant?.thirteenth_salary_month_2, activeTenant?.fourteenth_salary_month_13, activeTenant?.fourteenth_salary_month]
  );

  const extra = useMemo(() => extraMonths(activeTenant), [
    activeTenant?.thirteenth_salary_month, activeTenant?.thirteenth_salary_month_2,
    activeTenant?.fourteenth_salary_month_13, activeTenant?.fourteenth_salary_month,
  ]);
  const eff = useMemo(() => baseline.map(c => {
    let v = c.base;
    // Mouvements synthétiques (sans person_id : embauche/départ ouverts via monthly_amount)
    for (const mv of movements) {
      if (!mv.person_id) v += movementImpactOnCell(mv, c.year, c.monthIdx);
    }
    // Mouvements nominatifs (rate_change + departure liés à une personne)
    v += namedMovementDelta(contracts, movements, c.year, c.monthIdx, extra);
    return { ...c, value: v };
  }), [baseline, movements, contracts, extra]);

  // Mouvements pertinents pour la fenêtre roulante (start_month ∈ fenêtre)
  const windowMovements = useMemo(() => {
    const startCell = new Date(startYear, startMonthIdx, 1);
    const endCell   = new Date(eff[11]?.year, eff[11]?.monthIdx + 1, 0);
    return movements.filter(mv => {
      const sd = parseLocalDate(mv.start_month);
      return sd && sd >= startCell && sd <= endCell;
    });
  }, [movements, startYear, startMonthIdx, eff]);

  const totalBase    = baseline.reduce((a, c) => a + c.base, 0);
  const totalEff     = eff.reduce((a, c) => a + c.value, 0);
  const impact       = totalEff - totalBase;
  const avgMonthly   = totalEff / 12;

  const csEmprRate = Number(synth?.cs_fallback_rate_empr || 0);
  const csEmplRate = Number(synth?.cs_fallback_rate_empl || 0);
  // Weighted-average LPP employee rate, derived directly from hr_lpp_rates.
  // Excluded from cs_fallback_rate_empl on purpose (LPP is per-person), so
  // we add it here to make the NET realistic.
  const lppEmplPct = (() => {
    if (!lppRates.length) return 0;
    const sumRef = lppRates.reduce((a, r) => a + Number(r.reference_salary || 0), 0);
    const sumEmpl = lppRates.reduce((a, r) => a + Number(r.lpp_employee_chf || 0), 0);
    return sumRef > 0 ? (sumEmpl / sumRef) * 100 : 0;
  })();
  const totalCost  = totalEff * (1 + csEmprRate / 100);
  const netFactor  = 1 - (csEmplRate + lppEmplPct) / 100;

  const startLabel = `${monthLabel(startMonthIdx)} ${startYear}`;
  const endLabel   = `${monthLabel(eff[11]?.monthIdx)} ${eff[11]?.year}`;

  const maxBar = Math.max(...eff.map(c => c.value), 1);

  // ── CRUD ─────────────────────────────────────────────────────────────────
  async function addMovement(year, monthIdx, kind) {
    const payload = {
      movement_type: kind,
      start_month:   ymToDateStr(year, monthIdx),
      monthly_amount: DEFAULT_AMOUNT,
    };
    const r = await createPayrollMovement(tenantId, payload);
    if (r.error) setError(r.error);
    else setMovements(prev => [...prev, r.data]);
  }

  async function patchMv(mid, patch) {
    const r = await updatePayrollMovement(tenantId, mid, patch);
    if (r.error) setError(r.error);
    else setMovements(prev => prev.map(m => m.id === mid ? r.data : m));
  }

  async function removeMv(mid) {
    const r = await deletePayrollMovement(tenantId, mid);
    if (r.error) setError(r.error);
    else setMovements(prev => prev.filter(m => m.id !== mid));
  }

  async function clearAll() {
    if (movements.length === 0) return;
    if (!window.confirm(p('Supprimer les {{count}} mouvements (toutes années) ?', { count: movements.length }))) return;
    const r = await deleteAllPayrollMovements(tenantId);
    if (r.error) setError(r.error);
    else setMovements([]);
  }

  const yearOptions = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];

  if (!tenantId) return <main className="hr-page"><div className="hr-page__empty">Select a tenant first.</div></main>;

  const Container = embedded ? 'div' : 'main';
  return (
    <Container className={embedded ? 'hr-page is-embedded' : 'hr-page'}>
      <PageHeader
        title="Personnel cost forecast"
        actions={
          readOnly ? (
            <span className="hr-page__window-readonly" title={t('phrases:p_69d9d4840c')}>
              {startLabel} → {endLabel}
            </span>
          ) : (
            <>
              <label className="hr-page__year-picker">
                <span>Start</span>
                <select value={startMonthIdx} onChange={e => setStartMonthIdx(Number(e.target.value))}>
                  {MONTHS_SHORT.map((m, idx) => <option key={idx} value={idx}>{m}</option>)}
                </select>
              </label>
              <label className="hr-page__year-picker">
                <span>Year</span>
                <select value={startYear} onChange={e => setStartYear(Number(e.target.value))}>
                  {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </label>
              {setPage && !embedded && <button className="hr-page__back" onClick={() => setPage('home')}>← Home</button>}
            </>
          )
        }
      />

      {error && <div className="hr-page__error">{typeof error === 'string' ? error : (error.message || JSON.stringify(error))}</div>}
      {loading && <div className="hr-page__loading">Loading…</div>}
      {!loading && (
        <>
          <div className="hr-synth">
            <div className="hr-synth__head">SIMULATION 12 MOIS ROULANTS · {startLabel} → {endLabel}</div>
            <div className="hr-synth__grid hr-synth__grid--5">
              <KpiCell label={p('Salaires bruts 12M')}     value={formatNumber(totalEff, 0)}   hint={impact !== 0 ? `mouvements ${impact > 0 ? '+' : ''}${formatNumber(impact, 0)}` : 'baseline'} />
              <KpiCell label={p('Moyenne mensuelle')}      value={formatNumber(avgMonthly, 0)} />
              <KpiCell label={t('phrases:p_4ff08f6137')}   value={formatNumber(totalCost, 0)}  hint={`+${formatNumber(csEmprRate, 1)}% CS`} accent="primary" />
              <KpiCell label={t('phrases:p_4a7defada8')}   value={`${windowMovements.length}`} hint={`total DB : ${movements.length}`} />
              <KpiCell label={p('Δ vs baseline')}          value={`${impact >= 0 ? '+' : ''}${formatNumber(impact, 0)}`} hint={totalBase ? `${impact >= 0 ? '+' : ''}${formatNumber(impact / totalBase * 100, 1)}%` : '—'} accent={impact >= 0 ? 'primary' : 'terra'} />
            </div>
          </div>

          {/* Movements (gauche) + Chart (droite) ───────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 14, alignItems: 'start' }}>
            <div className="hr-chart" style={{ order: 2 }}>
              <div className="hr-chart__title">
                VARIATION DES SALAIRES BRUTS · {startLabel} → {endLabel}
                <span style={{ marginLeft: 12, color: 'var(--color-text-muted)', fontSize: 10, textTransform: 'none', letterSpacing: 0 }}>
                  + embauche · − départ · défaut {formatNumber(DEFAULT_AMOUNT, 0)} CHF
                </span>
              </div>
              <div className="hr-chart__bars" style={{ gridTemplateColumns: 'repeat(12, 1fr)', gap: 6 }}>
                {eff.map((c, i) => {
                  const delta = c.value - c.base;
                  // Segmented bar : base (sage) + delta (navy on hire, terracotta
                  // hatched on departure). Total height matches max(value, base)
                  // so the bar grows with hires and the "lost" part shows up
                  // above the new lower line on departures.
                  const topAmount = Math.max(c.value, c.base);
                  const baseAmount = Math.min(c.value, c.base);
                  const hTop = Math.max(2, (topAmount / maxBar) * 100);
                  const hBase = (baseAmount / maxBar) * 100;
                  const hDelta = hTop - hBase;
                  return (
                    <div className="hr-chart__col" key={i}>
                      <div className="hr-chart__val">
                        {formatNumber(c.value / 1000, 0)}k
                        {delta !== 0 && (
                          <span className={`hr-chart__val-delta ${delta > 0 ? 'is-up' : 'is-down'}`}>
                            {delta > 0 ? '+' : ''}{formatNumber(delta / 1000, 0)}k
                          </span>
                        )}
                      </div>
                      <div className="hr-chart__bar-wrap">
                        <div
                          className="hr-chart__bar"
                          style={{
                            height: `${hTop}%`,
                          }}
                          title={`${monthLabel(c.monthIdx)} ${c.year} — ${formatNumber(c.value, 0)} CHF${delta !== 0 ? ` (${delta > 0 ? '+' : ''}${formatNumber(delta, 0)} vs base)` : ''}`}
                        >
                          <div
                            className="hr-chart__bar-base"
                            style={{ height: `${hBase > 0 ? (hBase / hTop) * 100 : 0}%` }}
                          />
                          {delta !== 0 && (
                            <div
                              className={`hr-chart__bar-delta ${delta > 0 ? 'is-up' : 'is-down'}`}
                              style={{ height: `${(hDelta / hTop) * 100}%` }}
                            />
                          )}
                          {!readOnly && (
                            <>
                              <div className="hr-chart__btn-zone hr-chart__btn-zone--add" onClick={() => addMovement(c.year, c.monthIdx, 'hire')} title={p('Ajouter une embauche')}>+</div>
                              <div className="hr-chart__btn-zone hr-chart__btn-zone--sub" onClick={() => addMovement(c.year, c.monthIdx, 'departure')} title={t('phrases:p_533070fbee')}>−</div>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="hr-chart__month">
                        {monthLabel(c.monthIdx)}
                        <div style={{ fontSize: 8, color: 'var(--color-text-muted)', minHeight: 10 }}>
                          {(i === 0 || c.monthIdx === 0) ? c.year : ' '}
                        </div>
                      </div>
                      <div
                        className="hr-chart__net"
                        title={`Net = brut × (1 − ${csEmplRate.toFixed(2)} % CS base − ${lppEmplPct.toFixed(2)} % LPP empl.)`}
                      >
                        {formatNumber((c.value * netFactor) / 1000, 0)}k
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ order: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="hr-moves">
              <div className="hr-moves__head">
                <span className="hr-moves__title">Mouvements ({windowMovements.length} dans la fenêtre · {movements.length} total)</span>
                {!readOnly && movements.length > 0 && <button className="hr-moves__clear" onClick={clearAll}>Clear all</button>}
              </div>
              {windowMovements.length === 0 ? (
                <div className="hr-moves__empty">
                  {readOnly
                    ? <>Aucune embauche/départ planifié dans la fenêtre. Saisie dans <em>People</em>.</>
                    : <>Aucun mouvement dans la fenêtre. Clique <strong>+</strong> ou <strong>−</strong> sur une barre.</>}
                </div>
              ) : readOnly ? (
                <table className="hr-moves__table">
                  <tbody>
                    {windowMovements.map(mv => {
                      const { year: my, monthIdx } = dateStrToYM(mv.start_month);
                      const amt = movementDisplayAmount(mv, contracts, extra);
                      const sign = (mv.movement_type === 'departure' || amt < 0) ? -1 : +1;
                      const mName = monthLabel(monthIdx);
                      return (
                        <tr key={mv.id}>
                          <td style={{ width: 22, textAlign: 'center', fontWeight: 600, color: sign > 0 ? '#3D5129' : 'var(--terracotta-dark)' }}>
                            {sign > 0 ? '+' : '−'}
                          </td>
                          <td style={{ fontSize: 11 }}>{mName} {String(my).slice(2)}</td>
                          <td style={{ textAlign: 'right', fontSize: 12 }}>{formatNumber(Math.abs(amt), 0)}</td>
                          <td style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{mv.label || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <table className="hr-moves__table">
                  <tbody>
                    {windowMovements.map(mv => {
                      const { year: my, monthIdx } = dateStrToYM(mv.start_month);
                      const sign = mv.movement_type === 'departure' ? -1 : +1;
                      return (
                        <tr key={mv.id}>
                          <td style={{ width: 22, textAlign: 'center', fontWeight: 600, color: sign > 0 ? '#3D5129' : 'var(--terracotta-dark)' }}>
                            {sign > 0 ? '+' : '−'}
                          </td>
                          <td>
                            <select
                              value={`${my}-${monthIdx}`}
                              onChange={e => {
                                const [yy, mm] = e.target.value.split('-').map(Number);
                                patchMv(mv.id, { start_month: ymToDateStr(yy, mm) });
                              }}
                              style={{ fontSize: 11 }}
                            >
                              {eff.map((c, idx) => (
                                <option key={idx} value={`${c.year}-${c.monthIdx}`}>{monthLabel(c.monthIdx)} {String(c.year).slice(2)}</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {mv.person_id ? (
                              <span style={{ fontSize: 12 }} title={t('phrases:p_11fd809c6d')}>
                                {formatNumber(movementDisplayAmount(mv, contracts, extra), 0)}
                              </span>
                            ) : (
                              <input
                                type="number"
                                step="100"
                                value={Number(mv.monthly_amount || 0)}
                                onChange={e => patchMv(mv.id, { monthly_amount: e.target.value })}
                                style={{ textAlign: 'right', width: 80, fontSize: 12 }}
                              />
                            )}
                          </td>
                          <td>
                            <input
                              type="text"
                              value={mv.label || ''}
                              placeholder={t('phrases:p_89031778fa')}
                              onChange={e => patchMv(mv.id, { label: e.target.value })}
                              style={{ width: '100%', fontSize: 12 }}
                            />
                          </td>
                          <td>
                            <button className="hr-moves__remove" onClick={() => removeMv(mv.id)}>×</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            </div>
          </div>
        </>
      )}
    </Container>
  );
}

function KpiCell({ label, value, hint, accent, negative }) {
  return (
    <div className={`hr-kpi ${accent ? `hr-kpi--${accent}` : ''}`}>
      <div className="hr-kpi__label">{label}</div>
      <div className={`hr-kpi__value${negative ? ' hr-kpi__value--negative' : ''}`}>{value}</div>
      {hint && <div className="hr-kpi__hint">{hint}</div>}
    </div>
  );
}
